import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { TruncatedText } from '@client/components/_ui/display/TruncatedText';
import { User } from '@client/components/_ui/display/User';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { createColumnHelper, PrismaTable, type PrismaTableColumn, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Tooltip } from 'antd';
import { Table2 } from 'lucide-react';
import { useMemo, useState } from 'react';

// CUR-1232 — deleted registers, restorable by an org admin. There is no
// permanent delete: purging a register is out of scope, so a deleted register
// stays here until someone restores it.

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/trash/registers')({
    staticData: {
        metadata: { title: 'Registers' },
        featureFlag: 'enable-registers',
        modules: ['compliance'],
    },
    component: DeletedRegisters,
});

type QueryParams = RouterInputs['register']['listDeletedRegisters'];
type DeletedRegisterRow = RouterOutputs['register']['listDeletedRegisters']['rows'][number];

function useDeletedRegisterColumns({
    onRestore,
    restoringId,
}: {
    onRestore: (row: DeletedRegisterRow) => void;
    restoringId: bigint | null;
}): readonly PrismaTableColumn<DeletedRegisterRow, any, any>[] {
    const { formatDate } = useFormatter();

    return useMemo(() => {
        // No sortable columns: the server owns the order (newest deletion first).
        const columnHelper = createColumnHelper<DeletedRegisterRow, any>();
        return [
            columnHelper.display({
                id: 'name',
                header: 'Register',
                fixed: 'left',
                width: 280,
                hideable: false,
                cell: ({ row }) => (
                    <div className="flex min-w-0 items-center gap-2">
                        <Table2 size={16} className="flex-shrink-0 text-muted-foreground" />
                        <TruncatedText>{row.name}</TruncatedText>
                    </div>
                ),
            }),
            columnHelper.display({
                id: 'records',
                header: 'Records',
                width: 110,
                cell: ({ row }) => row._count.Records,
            }),
            columnHelper.display({
                id: 'deletedBy',
                header: 'Deleted by',
                width: 200,
                cell: ({ row }) =>
                    row.DeletedBy ? (
                        <User
                            userId={row.DeletedBy.userId}
                            photoUrl={row.DeletedBy.photoUrl}
                            name={row.DeletedBy.fullName}
                        />
                    ) : (
                        '—'
                    ),
            }),
            columnHelper.display({
                id: 'deletedAt',
                header: 'Deleted',
                width: 160,
                cell: ({ row }) => (row.deletedAt ? formatDate(row.deletedAt) : '—'),
            }),
            columnHelper.display({
                id: 'deletionReason',
                header: 'Reason',
                width: 280,
                cell: ({ row }) =>
                    row.deletionReason ? (
                        <Tooltip title={row.deletionReason}>
                            <span className="line-clamp-2">{row.deletionReason}</span>
                        </Tooltip>
                    ) : (
                        '—'
                    ),
            }),
            columnHelper.actions({
                width: 72,
                align: 'right',
                cell: ({ row }) => (
                    <Button.Refresh
                        tooltip="Restore"
                        loading={restoringId === row.id}
                        disabled={restoringId !== null && restoringId !== row.id}
                        onClick={(event) => {
                            event.stopPropagation();
                            onRestore(row);
                        }}
                    />
                ),
            }),
        ] as PrismaTableColumn<DeletedRegisterRow, any, any>[];
    }, [formatDate, onRestore, restoringId]);
}

function DeletedRegisters() {
    const { trpcUtils } = useTrpc();
    const [queryParams, setQueryParams] = useState<QueryParams>({ limit: DEFAULT_PAGE_SIZE, offset: 0 });
    const [restoringId, setRestoringId] = useState<bigint | null>(null);

    const { data, isFetching, error, refetch } = trpc.register.listDeletedRegisters.useQuery(queryParams, {
        meta: { errorSurface: 'inline' },
    });

    const restoreRegister = trpc.register.restoreRegister.useMutation({
        onSuccess: async (_result, { registerId }) => {
            const restored = data?.rows.find((row) => row.id === registerId);
            message.success(restored ? `${restored.name} restored` : 'Register restored');
            await Promise.all([
                trpcUtils.register.listDeletedRegisters.invalidate(),
                trpcUtils.register.listRegisters.invalidate(),
            ]);
        },
        // Restore can conflict with a register created since the delete (same
        // name or template) or with the org's register cap — the server message
        // says what to do, so show it as is.
        onError: (mutationError) => errorMessage.show(mutationError),
        onSettled: () => setRestoringId(null),
    });

    const columns = useDeletedRegisterColumns({
        restoringId,
        onRestore: (row) => {
            setRestoringId(row.id);
            restoreRegister.mutate({ registerId: row.id });
        },
    });

    const tableProps = usePrismaTable({
        data: data?.rows ?? [],
        total: data?.total ?? 0,
        loading: isFetching,
        columns,
        rowKey: (row) => row.id.toString(),
        queryParams,
        onParamsChange: setQueryParams,
        urlKey: 'q',
        queryError: error,
        pagination: { position: 'bottom', showSizeChanger: true },
        refreshable: { enabled: true, onRefresh: () => refetch() },
        emptyState: {
            noRecordsTitle: 'No deleted registers',
            noRecordsDescription: 'Registers you delete stay here until an admin restores them.',
        },
    });

    return (
        <PageController route={Route}>
            {error ? (
                <InlineQueryError
                    error={error}
                    onRetry={() => void refetch()}
                    title="Couldn't load deleted registers"
                />
            ) : (
                <PrismaTable {...tableProps} />
            )}
        </PageController>
    );
}
